import Box from "@mui/material/Box";
import { Link } from "react-router-dom";
import logo from "../data/images/GPML.png";
import * as routes from "../data/links/routes";
import { lazy, useState, useEffect } from "react";

const Stack = lazy(() => import("@mui/material/Stack"));
// ─────────────────────────────────────────
// Theme tokens
// ─────────────────────────────────────────
const C = {
  bg: "#0a0a0a",
  surface: "#111111",
  card: "#161616",
  border: "#1e1e1e",
  borderLight: "#252525",
  orange: "#f97316",
  orangeDark: "#ea580c",
  text: "#f5f5f5",
  textSub: "#b0b0b0",
  muted: "#555",
  dim: "#333",
  down: "#ff3b5c",
  up: "#22d47b",
};
// ─────────────────────────────────────────
// Step data
// ─────────────────────────────────────────
const STEPS_WEB = [
  { icon: "🔐", label: "Log in to your account at grindpartner.com" },
  { icon: "⚙️", label: "Open the Settings modal from the top navigation bar" },
  {
    icon: "🗑️",
    label: "Click the Delete Account button at the bottom of Settings",
  },
  {
    icon: "📧",
    label: "You will receive a confirmation email to your registered address",
    note: "Click the link in the email to finalise the deletion.",
    highlight: true,
  },
];

const STEPS_MOBILE = [
  { icon: "🔐", label: "Log in to your account on the iOS or Android app" },
  { icon: "⚙️", label: "Go to the Settings screen from the bottom navigation" },
  {
    icon: "🗑️",
    label: "Press Delete Account at the bottom of the Settings screen",
  },
  {
    icon: "📧",
    label: "You will receive a confirmation email to your registered address",
    note: "Click the link in the email to finalise the deletion.",
    highlight: true,
  },
];

const DeleteAccountInfo = () => {
  useKeyframes();
  const [glowVal, setGlowVal] = useState(8);

  useEffect(() => {
    let up = true;
    const id = setInterval(() => {
      setGlowVal((v) => {
        const n = up ? v + 0.15 : v - 0.15;
        if (n >= 20) up = false;
        if (n <= 6) up = true;
        return n;
      });
    }, 30);
    return () => clearInterval(id);
  }, []);

  return (
    <Stack
      sx={{
        minHeight: "100vh",
        background: C.bg,
        fontFamily: "DM Sans, sans-serif",
        color: C.text,
        position: "relative",
        overflowX: "hidden",
      }}
    >
      <Box
        sx={{
          position: "fixed",
          top: -140,
          left: "50%",
          transform: "translateX(-50%)",
          width: 700,
          height: 460,
          borderRadius: "50%",
          background:
            "radial-gradient(ellipse,rgba(249,115,22,.13) 0%,rgba(249,115,22,.04) 45%,transparent 70%)",
          pointerEvents: "none",
          zIndex: 0,
          animation: "gpGlow 4s ease-in-out infinite alternate",
        }}
      />
      <Box
        style={{
          position: "fixed",
          bottom: -160,
          right: -80,
          width: 400,
          height: 400,
          borderRadius: "50%",
          background:
            "radial-gradient(ellipse,rgba(255,59,92,.07) 0%,transparent 65%)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          maxWidth: 1120,
          margin: "0 auto",
          padding: "clamp(20px,4vw,48px) clamp(16px,4vw,40px) 60px",
          width: "100%",
        }}
      >
        <nav
          className="gp-fade-0"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "clamp(40px,6vw,72px)",
          }}
        >
          <Link
            to="/"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              textDecoration: "none",
            }}
          >
            <img
              src={logo}
              alt="GP Logo"
              style={{ width: 40, height: 40, borderRadius: "50%" }}
            />
            <div
              style={{
                fontFamily: "Oswald, sans-serif",
                fontWeight: 700,
                fontSize: 17,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: C.text,
              }}
            >
              Grind<span style={{ color: C.orange }}>&nbsp;Partner</span>
            </div>
          </Link>

          <a
            href="mailto:grindpartnerinfo@gmail.com"
            style={{
              padding: "7px 16px",
              borderRadius: 99,
              border: "1px solid rgba(249,115,22,.25)",
              background: "rgba(249,115,22,.07)",
              fontFamily: "DM Sans, sans-serif",
              fontSize: 12,
              fontWeight: 700,
              color: C.orange,
              textDecoration: "none",
              letterSpacing: "0.04em",
              transition: "all .2s",
            }}
            onMouseEnter={(e) => {
              e.target.style.background = "rgba(249,115,22,.14)";
              e.target.style.borderColor = "rgba(249,115,22,.5)";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "rgba(249,115,22,.07)";
              e.target.style.borderColor = "rgba(249,115,22,.25)";
            }}
          >
            Need Help?
          </a>
        </nav>
        {/* ── Hero section ── */}
        <div
          className="gp-fade-1"
          style={{ textAlign: "center", marginBottom: "clamp(32px,5vw,56px)" }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 16,
              padding: "6px 16px",
              borderRadius: 99,
              background: "rgba(255,59,92,0.08)",
              border: "1px solid rgba(255,59,92,0.25)",
              fontFamily: "DM Sans, sans-serif",
              fontSize: 11,
              fontWeight: 700,
              color: "#ff3b5c",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
            }}
          >
            <span>⚠️</span> Account Management
          </div>

          <h1
            style={{
              fontFamily: "Oswald, sans-serif",
              fontWeight: 900,
              fontSize: "clamp(36px,5.5vw,72px)",
              lineHeight: 0.95,
              letterSpacing: "-0.01em",
              textTransform: "uppercase",
              marginBottom: 14,
            }}
          >
            Delete Your
            <br />
            <span
              style={{
                WebkitTextFillColor: "transparent",
                background: "linear-gradient(135deg,#f97316 20%,#fbbf24 80%)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
              }}
            >
              Account
            </span>
          </h1>

          <p
            style={{
              fontFamily: "DM Sans, sans-serif",
              fontSize: "clamp(14px,1.4vw,17px)",
              color: C.muted,
              lineHeight: 1.7,
              maxWidth: 560,
              margin: "0 auto",
            }}
          >
            Follow the steps below to permanently delete your Grind Partner
            account. The process is the same for both platforms.
          </p>
        </div>

        {/* ── Warning banner ── */}
        <WarningBanner />

        {/* ── Platform columns ── */}
        <div
          className="gp-fade-4"
          style={{
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fit, minmax(min(100%,420px),1fr))",
            gap: 20,
            marginBottom: 48,
          }}
        >
          <PlatformCard
            platform="Web"
            icon="🖥️"
            steps={STEPS_WEB}
            delayBase={0}
            isMobile={false}
          />
          <PlatformCard
            platform="Mobile"
            icon="📱"
            steps={STEPS_MOBILE}
            delayBase={2}
            isMobile
            storeButtons={[
              {
                icon: "🤖",
                topLabel: "Get it on",
                name: "Google Play",
                href: process.env.REACT_APP_ANDROID_APP_URL,
              },
              {
                icon: "🍎",
                topLabel: "Download on the",
                name: "App Store",
                href: process.env.REACT_APP_IOS_APP_URL,
              },
            ]}
          />
        </div>

        {/* ── Contact card ── */}
        <div
          className="gp-fade-5"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 18,
            flexWrap: "wrap",
            padding: "22px 28px",
            borderRadius: 20,
            background: C.surface,
            border: `1px solid ${C.border}`,
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* stripe */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 2,
              background:
                "linear-gradient(90deg,#f97316,#ea580c,rgba(249,115,22,.04))",
            }}
          />
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 14,
              flexShrink: 0,
              background: "rgba(249,115,22,.1)",
              border: "1px solid rgba(249,115,22,.22)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 24,
            }}
          >
            💬
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div
              style={{
                fontFamily: "Oswald, sans-serif",
                fontWeight: 700,
                fontSize: 16,
                color: C.text,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                marginBottom: 4,
              }}
            >
              Need assistance?
            </div>
            <p
              style={{
                fontFamily: "DM Sans, sans-serif",
                fontSize: 13,
                color: C.muted,
                lineHeight: 1.6,
              }}
            >
              If you're having trouble deleting your account or haven't received
              the confirmation email, contact us directly.
            </p>
          </div>
          <a
            href="mailto:grindpartnerinfo@gmail.com"
            style={{
              display: "inline-block",
              padding: "11px 22px",
              borderRadius: 12,
              background: "linear-gradient(135deg,#f97316,#ea580c)",
              border: "none",
              fontFamily: "Oswald, sans-serif",
              fontWeight: 700,
              fontSize: 14,
              color: "#fff",
              letterSpacing: "0.06em",
              textDecoration: "none",
              whiteSpace: "nowrap",
              boxShadow: "0 4px 18px rgba(249,115,22,.35)",
              transition: "all .2s",
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = "translateY(-2px)";
              e.target.style.boxShadow = "0 8px 28px rgba(249,115,22,.45)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "none";
              e.target.style.boxShadow = "0 4px 18px rgba(249,115,22,.35)";
            }}
          >
            ✉️ Email Us
          </a>
        </div>

        {/* ── Footer ── */}
        <div
          style={{
            marginTop: 40,
            paddingTop: 24,
            borderTop: `1px solid ${C.border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 10,
          }}
        >
          <div
            style={{
              fontFamily: "DM Sans, sans-serif",
              fontSize: 12,
              color: C.muted,
            }}
          >
            <Link
              to={routes.LOGIN}
              style={{
                color: C.orange,
                textDecoration: "none",
                fontWeight: 700,
                marginRight: 16,
              }}
            >
              ← Back to App
            </Link>
            <Link
              to={routes.POLICY}
              style={{
                color: C.muted,
                textDecoration: "none",
                marginRight: 16,
              }}
            >
              Privacy Policy
            </Link>
            <Link
              to={routes.TERMS}
              style={{ color: C.muted, textDecoration: "none" }}
            >
              Terms of Use
            </Link>
          </div>
          <div
            style={{
              fontFamily: "Oswald, sans-serif",
              fontWeight: 700,
              fontSize: 11,
              letterSpacing: "0.12em",
              color: C.dim,
              textTransform: "uppercase",
            }}
          >
            © 2026 Grind Partner
          </div>
        </div>
      </Box>
    </Stack>
  );
};

export default DeleteAccountInfo;

// ─────────────────────────────────────────
// Global keyframes injected once
// ─────────────────────────────────────────
function useKeyframes() {
  useEffect(() => {
    if (document.getElementById("gp-del-kf")) return;
    const s = document.createElement("style");
    s.id = "gp-del-kf";
    s.textContent = `
      @import url('https://fonts.googleapis.com/css2?family=Oswald:wght@400;600;700;900&family=DM+Sans:wght@400;500;600;700&display=swap');
      @keyframes gpGlow    { from{opacity:.55;transform:translateX(-50%) scale(1)} to{opacity:1;transform:translateX(-50%) scale(1.1)} }
      @keyframes gpFadeUp  { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
      @keyframes gpDot     { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.3;transform:scale(.6)} }
      @keyframes gpPulse   { 0%,100%{box-shadow:0 0 0 0 rgba(255,59,92,.4)} 70%{box-shadow:0 0 0 8px rgba(255,59,92,0)} }
      @keyframes gpCountIn { from{opacity:0;transform:scale(.5) translateY(8px)} to{opacity:1;transform:scale(1) translateY(0)} }
      .gp-fade-0  { animation: gpFadeUp .6s ease .05s both; }
      .gp-fade-1  { animation: gpFadeUp .6s ease .15s both; }
      .gp-fade-2  { animation: gpFadeUp .6s ease .25s both; }
      .gp-fade-3  { animation: gpFadeUp .6s ease .35s both; }
      .gp-fade-4  { animation: gpFadeUp .6s ease .45s both; }
      .gp-fade-5  { animation: gpFadeUp .6s ease .55s both; }
      .gp-step-card { transition: border-color .2s, box-shadow .2s, transform .2s; }
      .gp-step-card:hover { border-color: rgba(249,115,22,.35) !important; box-shadow: 0 6px 24px rgba(249,115,22,.1) !important; transform: translateY(-2px); }
      .gp-store-btn { transition: all .2s ease; }
      .gp-store-btn:hover { border-color: rgba(249,115,22,.4) !important; transform: translateY(-2px); box-shadow: 0 8px 28px rgba(249,115,22,.14) !important; }
      * { box-sizing: border-box; margin: 0; padding: 0; }
      ::-webkit-scrollbar { width: 4px; }
      ::-webkit-scrollbar-thumb { background: #f97316; border-radius: 2px; }
    `;
    document.head.appendChild(s);
  }, []);
}

// ─────────────────────────────────────────
// Step card
// ─────────────────────────────────────────
function StepCard({ step, index, delay }) {
  return (
    <div
      className={`gp-step-card gp-fade-${delay}`}
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 16,
        padding: "16px 18px",
        borderRadius: 16,
        background: step.highlight ? "rgba(249,115,22,0.05)" : C.card,
        border: `1px solid ${step.highlight ? "rgba(249,115,22,0.25)" : C.border}`,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Left accent bar */}
      {step.highlight && (
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: 3,
            background: "linear-gradient(180deg,#f97316,#ea580c)",
            borderRadius: "3px 0 0 3px",
          }}
        />
      )}

      {/* Step number badge */}
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          flexShrink: 0,
          background: step.highlight
            ? "linear-gradient(135deg,#f97316,#ea580c)"
            : C.surface,
          border: `1px solid ${step.highlight ? "rgba(249,115,22,0.4)" : C.borderLight}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "Oswald, sans-serif",
          fontWeight: 700,
          fontSize: 14,
          color: step.highlight ? "#fff" : C.muted,
          boxShadow: step.highlight ? "0 0 12px rgba(249,115,22,0.35)" : "none",
          marginTop: 2,
        }}
      >
        {index + 1}
      </div>

      {/* Content */}
      <div style={{ flex: 1 }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
          <span style={{ fontSize: 20, marginTop: 1 }}>{step.icon}</span>
          <p
            style={{
              fontFamily: "DM Sans, sans-serif",
              fontSize: 14,
              color: step.highlight ? C.text : C.textSub,
              fontWeight: step.highlight ? 600 : 500,
              lineHeight: 1.65,
            }}
          >
            {step.label}
          </p>
        </div>
        {step.note && (
          <div
            style={{
              marginTop: 8,
              marginLeft: 30,
              padding: "6px 12px",
              borderRadius: 8,
              background: "rgba(249,115,22,0.07)",
              border: "1px solid rgba(249,115,22,0.18)",
              fontFamily: "DM Sans, sans-serif",
              fontSize: 12,
              color: C.orange,
              lineHeight: 1.55,
            }}
          >
            ℹ️ {step.note}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────
// Platform column
// ─────────────────────────────────────────
function PlatformCard({
  platform,
  icon,
  steps,
  storeButtons,
  delayBase,
  isMobile,
}) {
  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        background: C.surface,
        borderRadius: 24,
        border: `1px solid ${C.border}`,
        overflow: "hidden",
        boxShadow: "0 4px 32px rgba(0,0,0,0.4)",
      }}
    >
      {/* Accent stripe */}
      <div
        style={{
          height: 2,
          background:
            "linear-gradient(90deg,#f97316,#ea580c,rgba(249,115,22,.04))",
        }}
      />

      <div style={{ padding: "28px 24px 24px" }}>
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginBottom: 24,
          }}
        >
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 14,
              flexShrink: 0,
              background: "rgba(249,115,22,0.1)",
              border: "1px solid rgba(249,115,22,0.22)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 22,
            }}
          >
            {icon}
          </div>
          <div>
            <div
              style={{
                fontFamily: "Oswald, sans-serif",
                fontWeight: 700,
                fontSize: 20,
                color: C.text,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
              }}
            >
              {platform}
            </div>
            <div
              style={{
                fontFamily: "DM Sans, sans-serif",
                fontSize: 11,
                color: C.muted,
                marginTop: 2,
              }}
            >
              {isMobile ? "iOS & Android" : "grindpartner.com"}
            </div>
          </div>
        </div>

        {/* Steps */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 10,
            marginBottom: 24,
          }}
        >
          {steps.map((step, i) => (
            <StepCard key={i} step={step} index={i} delay={delayBase + i} />
          ))}
        </div>

        {/* Store buttons (mobile only) */}
        {storeButtons && (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {storeButtons.map((btn, i) => (
              <a
                key={i}
                target="_blank"
                href={btn.href || "#"}
                className="gp-store-btn"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "10px 16px",
                  borderRadius: 12,
                  border: `1px solid ${C.border}`,
                  background: C.card,
                  textDecoration: "none",
                  color: "inherit",
                  flex: 1,
                  minWidth: 130,
                }}
              >
                <span style={{ fontSize: 22 }}>{btn.icon}</span>
                <div>
                  <div
                    style={{
                      fontFamily: "DM Sans, sans-serif",
                      fontSize: 9,
                      fontWeight: 700,
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                      color: C.muted,
                      marginBottom: 2,
                    }}
                  >
                    {btn.topLabel}
                  </div>
                  <div
                    style={{
                      fontFamily: "Oswald, sans-serif",
                      fontWeight: 700,
                      fontSize: 15,
                      color: C.text,
                      letterSpacing: "0.04em",
                    }}
                  >
                    {btn.name}
                  </div>
                </div>
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────
// Warning banner
// ─────────────────────────────────────────
function WarningBanner() {
  return (
    <div
      className="gp-fade-3"
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 14,
        padding: "18px 22px",
        borderRadius: 16,
        background: "rgba(255,59,92,0.07)",
        border: "1px solid rgba(255,59,92,0.25)",
        marginBottom: 40,
        animation: "gpPulse 3s ease-in-out infinite",
      }}
    >
      <div
        style={{
          width: 38,
          height: 38,
          borderRadius: "50%",
          flexShrink: 0,
          background: "rgba(255,59,92,0.12)",
          border: "1px solid rgba(255,59,92,0.3)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 20,
        }}
      >
        ⚠️
      </div>
      <div>
        <div
          style={{
            fontFamily: "Oswald, sans-serif",
            fontWeight: 700,
            fontSize: 15,
            color: "#ff3b5c",
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            marginBottom: 4,
          }}
        >
          Permanent Action
        </div>
        <p
          style={{
            fontFamily: "DM Sans, sans-serif",
            fontSize: 13,
            color: C.textSub,
            lineHeight: 1.65,
          }}
        >
          Account deletion is{" "}
          <strong style={{ color: C.text }}>irreversible</strong>. All your
          workout history, personal bests, achievements, and leaderboard scores
          will be permanently removed. This action cannot be undone.
        </p>
      </div>
    </div>
  );
}
